import api from './client';
import { TIMEOUTS } from '../config/constants';

import type { AxiosResponse } from 'axios';

export type SpecKind = 'indicator' | 'strategy';

export interface ParamDefinitionV1 {
  id: string;
  engineKey: string;
  label: string;
  description: string;
  inputType: 'number' | 'percent' | 'select' | 'toggle' | 'timeframe' | 'money';
  unit?: string | null;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  default?: unknown;
  required: boolean;
  advanced: boolean;
  group: string;
  constraints: Record<string, unknown>[];
  visibilityRules: Record<string, unknown>[];
}

export interface DraftRequestV1 {
  specId: string;
  version?: string;
  name?: string;
  description?: string;
}

export interface EditorBundleV1 {
  schemaVersion: string;
  kind: SpecKind;
  specId: string;
  version: string;
  contract: Record<string, unknown>;
  scaffold: string;
  testsTemplate: string[];
  dependencyRules: Record<string, unknown>;
}

export interface DraftResponseV1 {
  requestId: string;
  schemaVersion: string;
  kind: SpecKind;
  draftSpec: Record<string, unknown>;
  editorBundle: EditorBundleV1;
}

export interface IndicatorSpecV1 {
  schemaVersion?: string;
  kind?: 'indicator';
  specId: string;
  version: string;
  name?: string;
  description?: string;
  status?: 'DRAFT' | 'BUILDING' | 'READY' | 'FAILED' | 'DEPRECATED';
  runtimeInterface?: 'IndicatorV1';
  warmupBars?: number;
  stateSchema?: Record<string, unknown>;
  paramDefinitions?: ParamDefinitionV1[];
  outputs?: Array<{ key: string; label: string; outputType: 'series' | 'scalar' | 'meta' }>;
  code?: {
    sourceCode?: string;
    entrypoint?: string;
    dependencies?: string[];
    language?: 'python';
  };
}

export interface StrategySpecV1 {
  schemaVersion?: string;
  kind?: 'strategy';
  specId: string;
  version: string;
  name?: string;
  description?: string;
  status?: 'DRAFT' | 'BUILDING' | 'READY' | 'FAILED' | 'DEPRECATED';
  strategyType?: string;
  runtimeInterface?: 'StrategyV1' | 'StrategyMultiAssetV2';
  nodes?: Array<{ nodeId: string; nodeType: string; config?: Record<string, unknown> }>;
  paramDefinitions?: ParamDefinitionV1[];
  defaultIndicators?: Record<string, unknown>;
  requiredContextFields?: string[];
  contextUsageMode?: 'signal_only' | 'risk_aware' | 'portfolio_aware';
  riskDependencies?: string[];
  code?: {
    sourceCode?: string;
    entrypoint?: string;
    dependencies?: string[];
    language?: 'python';
  };
}

export interface SaveResponseV1 {
  requestId: string;
  schemaVersion: string;
  kind: SpecKind;
  specId: string;
  version: string;
  status: string;
  digest: string;
}

export interface QuickSaveRequest {
  name: string;
  code: string;
}

export interface QuickSaveResponse {
  requestId: string;
  schemaVersion: string;
  kind: 'indicator' | 'strategy';
  specId: string;
  version: string;
  status: string;
  digest: string;
  extracted: Record<string, unknown>;
}

export interface ValidateResponseV1 {
  requestId: string;
  schemaVersion: string;
  valid: boolean;
  errors: string[];
  warnings: string[];
  hints: string[];
}

export interface DeprecateSpecRequestV1 {
  reason?: string;
  force?: boolean;
}

export interface DeprecateSpecResponseV1 {
  requestId: string;
  kind: SpecKind;
  specId: string;
  version: string;
  status: string;
  activeRefsCount: number;
  activeRefs: string[];
}

export interface CatalogItemV1 {
  specId: string;
  version: string;
  name: string;
  description: string;
  status: string;
  runtimeInterface: string;
  digest?: string;
  // Встроенные индикаторы: резолвятся по имени (без runtime_spec pin); несут категорию/outputs.
  builtin?: boolean;
  overlay?: boolean | null;
  category?: string;
  paramDefinitions?: Array<Record<string, unknown>>;
  outputs?: Array<{ key: string; label?: string; outputType?: string; scale?: string }>;
}

interface VersionsResponseV1 {
  versions: string[];
}

interface SpecResponseV1 {
  spec: Record<string, unknown>;
}

interface CatalogResponseV1 {
  items: CatalogItemV1[];
}

interface ResolvedStrategyResponseV1 {
  resolved: Record<string, unknown>;
}

export interface BaseSpecRefV1 {
  specId: string;
  version: string;
  kind?: SpecKind | null;
}

export interface ReferenceIndicatorRefV1 {
  specId: string;
  version: string;
}

export interface AiStatusResponseV1 {
  requestId: string;
  enabled: boolean;
  configured: boolean;
}

export interface AiContractResponseV1 {
  requestId: string;
  kind: SpecKind;
  contract: string;
  promptTemplate: string;
}

export interface AiChatSessionCreateRequestV1 {
  kind: SpecKind;
  title?: string;
  targetName?: string | null;
  baseSpecRef?: BaseSpecRefV1 | null;
  referenceIndicatorRef?: ReferenceIndicatorRefV1 | null;
  referenceIndicatorRefs?: ReferenceIndicatorRefV1[] | null;
}

export interface AiMessageV1 {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  model?: string | null;
  tokenUsage?: Record<string, unknown> | null;
  validationSummary?: Record<string, unknown> | null;
  createdAt?: string | null;
}

export interface AiArtifactV1 {
  id: string;
  sessionId: string;
  messageId?: string | null;
  kind: SpecKind;
  displayName: string;
  sourceCode: string;
  status: 'DRAFT' | 'VALID' | 'FAILED' | 'SAVED';
  specId?: string | null;
  version?: string | null;
  digest?: string | null;
  validationResult?: Record<string, unknown> | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AiChatSessionV1 {
  id: string;
  kind: SpecKind;
  title: string;
  status: string;
  targetName?: string | null;
  baseSpecId?: string | null;
  baseSpecVersion?: string | null;
  messages: AiMessageV1[];
  artifacts: AiArtifactV1[];
}

export interface AiChatMessageCreateRequestV1 {
  content: string;
  targetName?: string | null;
  referenceIndicatorRefs?: ReferenceIndicatorRefV1[] | null;
}

export interface AiChatMessageResponseV1 {
  requestId: string;
  assistantMessage: AiMessageV1;
  artifact?: AiArtifactV1 | null;
  openaiError?: string | null;
}

export interface AiPublishArtifactRequestV1 {
  publishName?: string | null;
}

export const tradingSpecsApi = {
  async createIndicatorDraft(body: DraftRequestV1): Promise<DraftResponseV1> {
    const resp: AxiosResponse<DraftResponseV1> = await api.post('/api/trading-specs/indicators/draft', body);
    return resp.data;
  },

  async createStrategyDraft(body: DraftRequestV1): Promise<DraftResponseV1> {
    const resp: AxiosResponse<DraftResponseV1> = await api.post('/api/trading-specs/strategies/draft', body);
    return resp.data;
  },

  async validateIndicator(body: IndicatorSpecV1): Promise<ValidateResponseV1> {
    const resp: AxiosResponse<ValidateResponseV1> = await api.post('/api/trading-specs/indicators/validate', body);
    return resp.data;
  },

  async validateStrategy(body: StrategySpecV1): Promise<ValidateResponseV1> {
    const resp: AxiosResponse<ValidateResponseV1> = await api.post('/api/trading-specs/strategies/validate', body);
    return resp.data;
  },

  async saveIndicator(body: IndicatorSpecV1): Promise<SaveResponseV1> {
    const resp: AxiosResponse<SaveResponseV1> = await api.post('/api/trading-specs/indicators', body);
    return resp.data;
  },

  async saveStrategy(body: StrategySpecV1): Promise<SaveResponseV1> {
    const resp: AxiosResponse<SaveResponseV1> = await api.post('/api/trading-specs/strategies', body);
    return resp.data;
  },

  async quickSaveIndicator(body: QuickSaveRequest): Promise<QuickSaveResponse> {
    const resp: AxiosResponse<QuickSaveResponse> = await api.post(
      '/api/trading-specs/indicators/quick',
      body,
      { timeout: TIMEOUTS.QUICK_SAVE, skipErrorNotification: true },
    );
    return resp.data;
  },

  async quickSaveStrategy(body: QuickSaveRequest): Promise<QuickSaveResponse> {
    const resp: AxiosResponse<QuickSaveResponse> = await api.post(
      '/api/trading-specs/strategies/quick',
      body,
      { timeout: TIMEOUTS.QUICK_SAVE, skipErrorNotification: true },
    );
    return resp.data;
  },

  async listVersions(kind: SpecKind, specId: string): Promise<string[]> {
    if (kind !== 'indicator' && kind !== 'strategy') return [];
    const sid = String(specId ?? '').trim();
    if (!sid) return [];
    const resp: AxiosResponse<VersionsResponseV1> = await api.get(
      `/api/trading-specs/${kind}/${encodeURIComponent(sid)}/versions`,
    );
    return resp.data?.versions ?? [];
  },

  async getSpecVersion(kind: SpecKind, specId: string, version: string): Promise<Record<string, unknown>> {
    if (kind !== 'indicator' && kind !== 'strategy') return {};
    const sid = String(specId ?? '').trim();
    const ver = String(version ?? '').trim();
    if (!sid || !ver) return {};
    const resp: AxiosResponse<SpecResponseV1> = await api.get(
      `/api/trading-specs/${kind}/${encodeURIComponent(sid)}/${encodeURIComponent(ver)}`,
    );
    return resp.data?.spec ?? {};
  },

  async deprecateSpecVersion(
    kind: SpecKind,
    specId: string,
    version: string,
    body: DeprecateSpecRequestV1,
  ): Promise<DeprecateSpecResponseV1> {
    const resp: AxiosResponse<DeprecateSpecResponseV1> = await api.post(
      `/api/trading-specs/${kind}/${encodeURIComponent(specId)}/${encodeURIComponent(version)}/deprecate`,
      body,
    );
    return resp.data;
  },

  async listIndicatorCatalog(status = 'READY'): Promise<CatalogItemV1[]> {
    const resp: AxiosResponse<CatalogResponseV1> = await api.get('/api/trading-specs/catalog/indicators', {
      params: { status },
    });
    return resp.data?.items ?? [];
  },

  async listStrategyCatalog(status = 'READY'): Promise<CatalogItemV1[]> {
    const resp: AxiosResponse<CatalogResponseV1> = await api.get('/api/trading-specs/catalog/strategies', {
      params: { status },
    });
    return resp.data?.items ?? [];
  },

  async resolveStrategy(specId: string, version: string, mode: 'agent' | 'backtest' | 'opt' = 'agent'): Promise<Record<string, unknown>> {
    const resp: AxiosResponse<ResolvedStrategyResponseV1> = await api.get(
      `/api/trading-specs/strategies/${encodeURIComponent(specId)}/${encodeURIComponent(version)}/resolved`,
      { params: { mode } },
    );
    return resp.data?.resolved ?? {};
  },

  async getSpecAiStatus(): Promise<AiStatusResponseV1> {
    const resp: AxiosResponse<AiStatusResponseV1> = await api.get('/api/trading-specs/ai/status');
    return resp.data;
  },

  async getSpecAiContract(kind: SpecKind): Promise<AiContractResponseV1> {
    const resp: AxiosResponse<AiContractResponseV1> = await api.get('/api/trading-specs/ai/contracts', {
      params: { kind },
    });
    return resp.data;
  },

  async createSpecAiSession(body: AiChatSessionCreateRequestV1): Promise<{ session: AiChatSessionV1 }> {
    const resp: AxiosResponse<{ requestId: string; session: AiChatSessionV1 }> = await api.post(
      '/api/trading-specs/ai/sessions',
      body,
      { timeout: TIMEOUTS.AI },
    );
    return { session: resp.data.session };
  },

  async getSpecAiSession(sessionId: string): Promise<AiChatSessionV1> {
    const resp: AxiosResponse<{ requestId: string; session: AiChatSessionV1 }> = await api.get(
      `/api/trading-specs/ai/sessions/${encodeURIComponent(sessionId)}`,
    );
    return resp.data.session;
  },

  async sendSpecAiMessage(
    sessionId: string,
    body: AiChatMessageCreateRequestV1,
  ): Promise<AiChatMessageResponseV1> {
    const resp: AxiosResponse<AiChatMessageResponseV1> = await api.post(
      `/api/trading-specs/ai/sessions/${encodeURIComponent(sessionId)}/messages`,
      body,
      { timeout: TIMEOUTS.AI },
    );
    return resp.data;
  },

  async validateSpecAiArtifact(artifactId: string): Promise<AiArtifactV1> {
    const resp: AxiosResponse<{ requestId: string; artifact: AiArtifactV1 }> = await api.post(
      `/api/trading-specs/ai/artifacts/${encodeURIComponent(artifactId)}/validate`,
      undefined,
      { timeout: TIMEOUTS.AI },
    );
    return resp.data.artifact;
  },

  async publishSpecAiArtifact(
    artifactId: string,
    body: AiPublishArtifactRequestV1 = {},
  ): Promise<AiArtifactV1> {
    const resp: AxiosResponse<{ requestId: string; artifact: AiArtifactV1 }> = await api.post(
      `/api/trading-specs/ai/artifacts/${encodeURIComponent(artifactId)}/publish`,
      body,
      { timeout: TIMEOUTS.AI },
    );
    return resp.data.artifact;
  },
};

